import { cloneDeep, debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import { convertComparisonTypeToString } from '../../continuous-scroller/continuous-scroller-functions';
import type {
  ContinuousScrollerComparisonType,
  ContinuousScrollerFilter,
} from '../../continuous-scroller/continuous-scroller-types';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { DataGridFilterProps } from './data-grid-filter.interface';
import DataGridNumericFilterPanel, { comparisonDropDownData } from './data-grid-numeric-filter-panel.component';

export interface DataGridNumericFilterProps extends DataGridFilterProps<number> {
  type?: 'number' | 'decimal';
  debounceTime?: number;
}

export default function DataGridNumericFilter({
  type,
  column,
  filterSignal,
  debounceTime = 1000,
  forceResetColumnMap,
}: DataGridNumericFilterProps) {
  const [currentValue, setCurrentValue] = useState<string | Array<string>>((column.searchValue as string) ?? '');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const previousColumn = useRef<DataGridColumnSettings | undefined>(undefined);
  const columnRef = useRef<HTMLDivElement | null>(null);

  const tagCreator = useTestTag(column.title || '');
  const combinedRef = combineRefs(columnRef, tagCreator('numeric-filter'));

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      setCurrentValue((column.searchValue as string) ?? '');
    }

    previousColumn.current = column;
  }, [column, forceResetColumnMap]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect to initialize filter from initial column values
  useEffect(() => {
    if (currentValue.length > 0) {
      const clonedFilters = cloneDeep(filterSignal.value);
      clonedFilters.push({
        scrollerId: column.id,
        field: column.field,
        customDataProperties: column.customDataProperties,
        value: parseFilter(currentValue),
        comparison: column.searchComparison,
      });
      filterSignal.value = clonedFilters;
    }
  }, []);

  const parseFilter = (filter: string | Array<string>) => {
    const getValue = (value: string) => (type === 'number' ? parseInt(value) : parseFloat(value));
    if (Array.isArray(filter)) {
      return filter.map((value) => getValue(value));
    } else {
      return getValue(filter);
    }
  };

  const onFilterChange = useCallback(
    debounce((filter: ContinuousScrollerFilter) => {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
      const clonedFilters = cloneDeep(filterSignal.value);

      if (filter.value.toString().length > 0) {
        filter.value = parseFilter(filter.value as string | Array<string>);

        if (filterIndex > -1) {
          clonedFilters.splice(filterIndex, 1, filter);
        } else {
          clonedFilters.push(filter);
        }

        filterSignal.value = clonedFilters;
        setCurrentValue(String(filter.value));
      } else {
        if (filterIndex > -1) {
          onClearFilter();
        }
      }
    }, debounceTime),
    []
  );

  const onClearFilter = () => {
    filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
    setCurrentValue('');
  };

  const displayValue = () => {
    const activeColumnFilter = filterSignal.value.find((filter) => filter.scrollerId === column.id);

    if (activeColumnFilter && activeColumnFilter.comparison && currentValue && currentValue.length > 0) {
      return `${resolveComparisonDisplayValue(activeColumnFilter.comparison)} ${currentValue}`;
    }
  };

  return (
    <div ref={combinedRef} className="h-full">
      <DataGridNumericFilterPanel
        column={column}
        currentValue={currentValue}
        open={isFilterPanelOpen}
        onOpenChange={setIsFilterPanelOpen}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilter}
        displayValue={displayValue()}
      />
    </div>
  );
}

function resolveComparisonDisplayValue(value: ContinuousScrollerComparisonType) {
  const appliedFilter = comparisonDropDownData.find((item) => item.value === value);
  if (appliedFilter) {
    return appliedFilter.text;
  }
  return convertComparisonTypeToString(value);
}
